// /portal/mfa/challenge — standalone re-challenge page (post-12h freshness
// expiry). Despite the user already being signed in, we render the same
// centered "premium" auth chrome (no AppShell, no sidebar) so the
// verification step feels intentional and standalone, matching the
// post-password challenge step on /users/sign_in.
//
// The factor-picker UI lives in components/domain/mfa-challenge.tsx and
// is shared with the SignIn page.
//
// No "Password accepted" success banner here — the user landed on this
// page from a session-freshness expiry, not a fresh password event.

import { usePage } from "@inertiajs/react"
import { LogoMark } from "@/components/layout/logo-mark"
import { MfaChallenge } from "@/components/domain/mfa-challenge"
import type { SharedProps } from "@/types/inertia"

type Props = {
  hasPasskey:     boolean
  hasTotp:        boolean
  hasRecovery:    boolean
  passkeyOptions: Record<string, unknown> | null
}

export default function MfaChallengePage(props: Props) {
  const { props: shared } = usePage<SharedProps>()
  const flash = shared.flash

  return (
    <div className="flex min-h-screen items-center justify-center bg-app-bg px-6 py-12 text-app-text">
      <div className="w-full max-w-2xl space-y-8">
        <header className="flex flex-col items-center gap-5">
          <LogoMark size="hero" />
          <div className="text-center">
            <h1 className="text-3xl font-semibold tracking-tight text-app-text">Finish signing in</h1>
            <p className="mt-2 text-sm text-app-muted">Choose a verification method to confirm it&rsquo;s you.</p>
          </div>
        </header>

        {flash.notice && (
          <div className="rounded-control border border-status-success/30 bg-status-success/10 px-4 py-2 text-sm text-status-success">
            {flash.notice}
          </div>
        )}
        {flash.alert && (
          <div className="rounded-control border border-status-error/30 bg-status-error/10 px-4 py-2 text-sm text-status-error">
            {flash.alert}
          </div>
        )}

        <MfaChallenge {...props} />

        <p className="text-center text-xs text-app-subtle">
          Trouble signing in?{" "}
          <a href="mailto:support@valet.red" className="text-brand-red hover:underline">Contact support</a>
        </p>
      </div>
    </div>
  )
}
